<template>
  <div class="modal-overlay" role="dialog" aria-modal="true">
    <div class="mmodal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          {{ mode === "create" ? "Add New Setting" : "Edit Setting" }}
        </h5>
        <button
          type="button"
          class="close"
          @click="closeModal"
          aria-label="Close modal"
        >
          &times;
        </button>
      </div>

      <div class="modal-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          Loading settings configuration...
        </div>

        <!-- Form Content -->
        <form v-else @submit.prevent="saveModal" class="form-grid">
          <!-- 1. Category Selection -->
          <div class="form-group">
            <label class="form-label" for="settingCategory">Category</label>
            <select
              id="settingCategory"
              class="form-control"
              v-model="selectedCategory"
              @change="onCategoryChange"
              :disabled="mode === 'edit' && !!editData"
            >
              <option value="">-- Select Category --</option>
              <option
                v-for="(catDef, catKey) in settingsTree"
                :key="catKey"
                :value="catKey"
              >
                {{ catDef.category_name || catKey }}
              </option>
            </select>
          </div>

          <!-- 2. Setting Name Selection -->
          <div class="form-group">
            <label class="form-label" for="settingName">Setting</label>
            <select
              id="settingName"
              class="form-control"
              v-model="selectedSetting"
              @change="onSettingChange"
              :disabled="!selectedCategory || (mode === 'edit' && !!editData)"
            >
              <option value="">-- Select Setting --</option>
              <option
                v-for="(childDef, childKey) in currentCategoryChildrenObject"
                :key="childKey"
                :value="childKey"
              >
                {{ childDef.setting_name || childKey }}
              </option>
            </select>
          </div>

          <!-- 3. Setting Key (Readonly) -->
          <div class="form-group">
            <label class="form-label">Key (Read-Only)</label>
            <input
              type="text"
              class="form-control"
              :value="form.setting_key"
              readonly
              placeholder="Auto-populated"
            />
          </div>

          <!-- 4. Description (Readonly) -->
          <div class="form-group">
            <label class="form-label">Description (Read-Only)</label>
            <textarea
              class="form-control"
              rows="2"
              :value="form.description"
              readonly
              placeholder="Auto-populated"
            />
          </div>

          <!-- 5. Value: Full width, dynamic input (ALWAYS after setting inputs) -->
          <div class="form-group full-width">
            <label class="form-label" for="settingValue">Value</label>

            <!-- Dynamic Input Rendering based on selectedDefinition.input_type -->

            <!-- SELECT Input Type -->
            <select
              v-if="selectedDefinition.input_type === 'select'"
              id="settingValue"
              class="form-control"
              v-model="form.setting_value"
            >
              <option value="">-- Select Value --</option>
              <option
                v-for="opt in selectedDefinition.possible_values || []"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>

            <!-- TEXT Input Type (Default/Fallback) -->
            <input
              v-else-if="
                selectedDefinition.input_type === 'text' ||
                !selectedDefinition.input_type
              "
              id="settingValue"
              type="text"
              class="form-control"
              v-model="form.setting_value"
            />

            <!-- NUMBER Input Type -->
            <input
              v-else-if="selectedDefinition.input_type === 'number'"
              id="settingValue"
              type="number"
              class="form-control"
              v-model.number="form.setting_value"
            />

            <!-- TEXTAREA Input Type -->
            <textarea
              v-else-if="selectedDefinition.input_type === 'textarea'"
              id="settingValue"
              class="form-control"
              rows="3"
              v-model="form.setting_value"
            />

            <!-- Fallback for unsupported types -->
            <input
              v-else
              id="settingValue"
              type="text"
              class="form-control"
              v-model="form.setting_value"
            />
          </div>

          <!-- 6. Status Control -->
          <div class="form-group full-width align-right">
            <label class="form-label me-2" for="settingStatus">Status</label>
            <select
              id="settingStatus"
              v-model.number="form.status"
              class="form-control small-select"
            >
              <option :value="1">Active</option>
              <option :value="0">Inactive</option>
            </select>
          </div>
        </form>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" @click="closeModal">
          Cancel
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          @click="saveModal"
          :disabled="isLoading || !form.setting_key"
        >
          {{ mode === "create" ? "Save Setting" : "Update Setting" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../api/apiIndex";

export default {
  name: "SettingModal",

  props: {
    mode: {
      type: String,
      default: "create", // 'create' or 'edit'
    },
    editData: {
      type: Object,
      default: null,
    },
    // The settingsTree prop is removed as the component now fetches it internally
  },

  data() {
    return {
      // State for fetched data
      settingsTree: {},
      savedSettings: [],
      isLoading: false,

      // State for form selections and definition
      selectedCategory: "",
      selectedSetting: "",
      selectedDefinition: {}, // Holds the definition from settingsTree for the selected setting

      // Data model for the setting being created/edited
      form: {
        category: "",
        setting_name: "",
        setting_key: "",
        setting_value: "",
        description: "",
        status: 1, // Default to Active
      },
    };
  },

  computed: {
    /**
     * Retrieves the children object (settings) for the currently selected category.
     */
    currentCategoryChildrenObject() {
      if (!this.selectedCategory) return {};
      const cat = this.settingsTree[this.selectedCategory];
      return cat?.children || {};
    },
  },

  methods: {
    closeModal() {
      this.$emit("close");
    },

    /**
     * API call to fetch the settings configuration.
     * Assumes the API endpoint '/company-settings/index' is accessible via GET at a path like 'company-settings-data'.
     */
    async fetchSettingsData() {
      this.isLoading = true;
      try {
        // Assuming 'company-settings-data' is the endpoint for the provided index() function
        const response = await generalApiRequest(
          "company-settings",
          "GET",
          {},
          1
        );

        this.settingsTree = response.settings_tree || {};
        this.savedSettings = response.saved_settings || [];

        // If in edit mode, attempt to prefill the form now that data is loaded
        if (this.mode === "edit" && this.editData) {
          this.fillEditData();
        }
      } catch (error) {
        console.error("Error fetching settings configuration:", error);
        // Display a simple error to the user
        alert(
          "Failed to load settings configuration. Check console for details."
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Handles API call for saving or updating the setting.
     */
    async saveModal() {
      if (!this.form.setting_key) {
        alert("Please select a setting to save.");
        return;
      }

      try {
        let url = "";
        let method = "";
        const payload = {
          setting_category_name: this.form.category,
          setting_key: this.form.setting_key,
          setting_name: this.form.setting_name,
          setting_value: this.form.setting_value,
          description: this.form.description,
          status: this.form.status,
          // 'category' is usually not required for DB save, but included for context if needed
          category: this.form.category,
        };

        if (this.mode === "create") {
          url = "company-settings";
          method = "POST";
        } else {
          if (!this.editData || !this.editData.id) {
            alert("Error: Missing ID for update operation.");
            return;
          }
          url = `company-settings/${this.editData.id}`;
          method = "PUT";
        }

        // console.log("++++++++++++++++++++++++++++++++++++");
        // console.log(url);

        await generalApiRequest(url, method, payload, 1);
        this.$emit("saved");
      } catch (err) {
        console.error("Save Modal Error:", err);
        alert("Failed to save or update setting.");
      }
    },

    /**
     * Resets dependent fields when category is changed.
     */
    onCategoryChange() {
      // Reset setting selection and dependent fields
      this.selectedSetting = "";
      this.selectedDefinition = {};
      this.form.setting_key = "";
      this.form.setting_name = "";
      this.form.setting_value = "";
      this.form.description = "";
      // Update form's category key
      this.form.category = this.selectedCategory || "";
    },

    /**
     * Populates definition fields (key, name, description, default value) when a setting is selected.
     */
    onSettingChange() {
      if (!this.selectedCategory || !this.selectedSetting) {
        this.selectedDefinition = {};
        return;
      }

      const def = this.currentCategoryChildrenObject[this.selectedSetting];
      if (!def) {
        this.selectedDefinition = {};
        return;
      }

      this.selectedDefinition = def;

      // Autopopulate read-only fields
      this.form.category = this.selectedCategory;
      this.form.setting_name = def.setting_name || "";
      this.form.setting_key = def.setting_key || "";
      this.form.description = def.description || "";

      // Set default value only when creating a new setting
      if (this.mode === "create") {
        if (Object.prototype.hasOwnProperty.call(def, "default_value")) {
          this.form.setting_value = def.default_value;
        } else if (
          def.input_type === "select" &&
          Array.isArray(def.possible_values) &&
          def.possible_values.length > 0
        ) {
          this.form.setting_value = def.possible_values[0].value;
        } else {
          this.form.setting_value = "";
        }
      }
    },

    /**
     * Initializes the form fields when in 'edit' mode using data from editData.
     */
    fillEditData() {
      if (!this.editData || !this.settingsTree) return;

      const savedKey = this.editData.setting_key;
      let foundCategoryKey = null;
      let foundSettingKey = null;

      // Search the settingsTree definition using the saved setting_key
      for (const [catKey, catDef] of Object.entries(this.settingsTree || {})) {
        if (!catDef || !catDef.children) continue;
        for (const [childKey, childDef] of Object.entries(catDef.children)) {
          if (childDef?.setting_key === savedKey) {
            foundCategoryKey = catKey;
            foundSettingKey = childKey;
            this.selectedDefinition = childDef; // Store the definition found
            break;
          }
        }
        if (foundCategoryKey) break;
      }

      // Apply the identified keys
      this.selectedCategory = foundCategoryKey || "";
      this.selectedSetting = foundSettingKey || "";

      // Manually populate form fields based on the definition found
      if (this.selectedCategory && this.selectedSetting) {
        this.form.category = this.selectedCategory;
        this.form.setting_name = this.selectedDefinition.setting_name || "";
        this.form.setting_key = this.selectedDefinition.setting_key || "";
        this.form.description = this.selectedDefinition.description || "";
      }

      // Override value & status with saved DB values
      this.form.setting_value = this.editData.setting_value ?? "";
      this.form.status =
        this.editData.status !== undefined ? this.editData.status : 1;
    },
  },

  mounted() {
    this.fetchSettingsData();
  },
};
</script>

<style scoped>
/* Base Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.mmodal-content {
  background: #ffffff;
  border-radius: 10px;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

/* Header */
.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f7f7f7;
}

.modal-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a202c;
}

.close {
  background: none;
  border: none;
  color: #a0aec0;
  font-size: 24px;
  cursor: pointer;
  transition: color 0.2s;
}

.close:hover {
  color: #d9534f;
}

/* Body and Form Layout */
.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  align-items: start;
}

.form-group {
  display: flex;
  flex-direction: column;
}
.form-label {
  font-size: 1rem;
  margin-bottom: 8px;
  color: #4a5568;
  font-weight: 600;
}

.form-control {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
  width: 100%;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 1px #4299e1;
  outline: none;
}

/* Specific Layout Helpers */
.full-width {
  grid-column: 1 / -1;
}

.align-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.small-select {
  width: 140px;
}

/* Footer */
.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;
}

.btn-primary {
  background-color: #4299e1;
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: #3182ce;
}

.btn-secondary {
  background-color: #e2e8f0;
  color: #4a5568;
  border: 1px solid #cbd5e0;
}

.btn-secondary:hover {
  background-color: #cbd5e0;
}

/* Loading State */
.loading-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 50px 0;
  font-size: 1.1rem;
  color: #4a5568;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4299e1;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive adjustments */
@media (max-width: 700px) {
  .mmodal-content {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
  .form-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  .align-right {
    justify-content: space-between;
    width: 100%;
  }
  .small-select {
    width: 50%;
  }
}
</style>
